import React from "react";
import { motion } from "framer-motion";
import { Box, Typography, Stack, Button, Tooltip } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SettingsIcon from "@mui/icons-material/Settings";
import { StepContainer } from "../styles";
import { stepContent } from "../content";
import { ModernButton } from "../../styled/button";

const FinalStep = ({ onComplete, onNavigate, canNavigate = true }) => {
  const theme = useTheme();
  const content = stepContent.final;

  return (
    <StepContainer>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Success Icon with Animation */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              delay: 0.2,
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 80,
                color: "success.main",
                mb: 2,
                filter: `drop-shadow(0 4px 16px ${alpha(
                  theme.palette.success.main,
                  0.4
                )})`,
              }}
            />
          </motion.div>

          <Typography variant="h3" fontWeight={700} gutterBottom>
            {content.title}
          </Typography>
          <Typography variant="h6" color="text.secondary" fontWeight={500}>
            {content.subtitle}
          </Typography>
        </Box>

        {/* Message */}
        <Box
          sx={{
            p: 3,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.success.main, 0.08),
            border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
            mb: 4,
          }}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ lineHeight: 1.8 }}
          >
            {content.message}
          </Typography>
        </Box>

        {/* Features Summary */}
        <Stack spacing={2} sx={{ mb: 4, maxWidth: 500, mx: "auto" }}>
          {[
            "✅ Cardápio Digital",
            "✅ Configuração de Operação",
            "✅ Gestão de Entregas",
            "✅ Integrações com Plataformas",
            "✅ Módulo Fiscal",
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
            >
              <Typography
                variant="body1"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.grey[500], 0.05),
                }}
              >
                {item}
              </Typography>
            </motion.div>
          ))}
        </Stack>

        {/* CTAs */}
        <Stack spacing={2} sx={{ mt: "auto" }}>
          <ModernButton
            onClick={onComplete}
            size="large"
            fullWidth
            endIcon={<RocketLaunchIcon />}
          >
            {content.primaryCta}
          </ModernButton>

          <Tooltip
            title={!canNavigate ? "Apenas administradores podem acessar esta configuracao" : ""}
            placement="top"
          >
            <span>
              <Button
                onClick={() => onNavigate && onNavigate(content.secondaryRoute)}
                size="large"
                fullWidth
                variant="outlined"
                startIcon={<SettingsIcon />}
                disabled={!canNavigate}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {content.secondaryCta}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </motion.div>
    </StepContainer>
  );
};

export default FinalStep;
